import React, { useState, useEffect } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import axios from "axios";
import { getToken, base_url } from "../../config/config";
import { ToastContainer, toast } from "react-toastify";
import {
  FaBoxOpen,
  FaTools,
  FaCogs,
  FaIndustry,
  FaWrench,
  FaRecycle,
  FaCut,
  FaFire,
  FaHammer,
  FaPaintBrush,
  FaBoxes,
  FaTruck,
  FaCheckSquare,
  FaEllipsisH,
  FaFileAlt,
  FaChevronUp,
  FaTemperatureLow,
  FaLandmark,
  FaCoffee,
} from "react-icons/fa";
import { Download, Upload, Save, Trash2 } from "lucide-react";
import { useTheme } from "@mui/material";

const ItemTypes = {
  NAME: "name",
  PROCESS: "process",
};

const TemplateMaker = () => {
  const theme = useTheme();
  // --- State & Data ---
  const [employees, setEmployees] = useState([]);
  const [lieferanten, setLieferanten] = useState([]);
  const [employeeMachines, setEmployeeMachines] = useState([]);
  const [allMachines, setAllMachines] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [availableProcesses] = useState([
    {
      id: 1,
      label:
        "Materialeingang / Materialausgang mit Kontrolle und Weitergabe an die Produktion incl. Auftragsnummer",
    },
    { id: 2, label: "Baugruppe montieren" },
    { id: 3, label: "Maschine einrichten" },
    { id: 4, label: "CNC Fräsen" },
    { id: 5, label: "Konventionell Fräsen" },
    { id: 6, label: "CNC Drehen" },
    { id: 7, label: "Konventionell Drehen" },
    { id: 8, label: "Schweißen" },
    { id: 9, label: "Sägen" },
    { id: 10, label: "Konservieren / Lackieren" },
    { id: 11, label: "Verpacken / Versenden" },
    { id: 12, label: "Transport innerhalb der Firma" },
    { id: 13, label: "Qualitätskontrolle" },
    { id: 14, label: "Sonstiges" },
    { id: 15, label: "Angaben Formulare zu re übergeben" },
    { id: 16, label: "Stoßen" },
    { id: 17, label: "Normalisieren / Härten" },
    { id: 18, label: "Löten" },
    { id: 0, label: "Unterbrechung", isBreak: true },
  ]);

  // Colors for processes (none are purple)
  const processColors = {
    1: "#005B96",
    2: "orange",
    3: "teal",
    4: "#1FD655",
    5: "#649200",
    6: "#a2afb0",
    7: "grey",
    8: "#95CBE2",
    9: "#844204",
    10: "#680000",
    11: "#E9D700",
    12: "#1E64AF",
    13: "green",
    14: "#557882",
    15: "pink",
    16: "#60CDB8",
    17: "#373737",
    break: "#FFC107",
  };

  // Icons for processes
  const processIcons = {
    1: FaBoxOpen, // Material input/output
    2: FaTools, // Assembly
    3: FaCogs, // Machine setup
    4: FaIndustry, // CNC Milling
    5: FaWrench, // Conventional Milling
    6: FaIndustry, // CNC Turning
    7: FaWrench, // Conventional Turning
    8: FaFire, // Welding
    9: FaCut, // Sawing
    10: FaPaintBrush, // Conserving/Painting
    11: FaBoxes, // Packing/Shipping
    12: FaTruck, // Transport within company
    13: FaCheckSquare, // Quality control
    14: FaEllipsisH, // Other
    15: FaFileAlt, // Providing forms
    16: FaChevronUp, // Pushing
    17: FaTemperatureLow, // Normalizing/Hardening
    18: FaLandmark, // Soldering
    0: FaCoffee, // Break
    break: FaCoffee, // Break (alternative reference)
  };

  const [templateName, setTemplateName] = useState("");
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [showMachineModal, setShowMachineModal] = useState(false);
  const [selectedEmployeeForMachine, setSelectedEmployeeForMachine] =
    useState(null);
  const [selectedProcessIndex, setSelectedProcessIndex] = useState(null);
  const [apiBaseUrl, setApiBaseUrl] = useState(base_url);

  // State for machine assignment
  const [filteredMachines, setFilteredMachines] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedImportTemplate, setSelectedImportTemplate] = useState(null);
  const [lieferantSearch, setLieferantSearch] = useState("");
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [noMachineSelected, setNoMachineSelected] = useState(false);

  // --- Fetch Data ---
  // Function to fetch templates
  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/v1/GetCaledarTemplate`);
      setSavedTemplates(response.data);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  // Function to delete a template
  const handleDeleteTemplate = async (templateId, e) => {
    e.stopPropagation(); // Prevent selecting the template when clicking delete

    if (
      !window.confirm(
        "Sind Sie sicher, dass Sie diese Vorlage löschen möchten?",
      )
    ) {
      return;
    }

    try {
      await axios.delete(`${apiBaseUrl}/v1/DeleteTemplate/${templateId}`);
      toast.success("Vorlage erfolgreich gelöscht!");
      // Refresh templates list
      fetchTemplates();
      // Clear selection if the deleted template was selected
      if (selectedImportTemplate?._id === templateId) {
        setSelectedImportTemplate(null);
      }
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Fehler beim Löschen der Vorlage");
    }
  };

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        // We don't need the includeFormer parameter since the backend will filter them out by default
        const response = await axios.get(`${apiBaseUrl}/v1/getAllEmployees`);

        // Apply additional client-side filtering for active employees
        const filteredEmployees = response.data.filter((employee) => {
          // Check if employee is active
          if (employee.isActive === false) return false;

          // Check if employee is marked as former
          if (employee.isFormerEmployee === true) return false;

          // Check employment date constraints
          const today = new Date();

          // If employment hasn't started yet, exclude
          if (
            employee.employmentStartDate &&
            new Date(employee.employmentStartDate) > today
          ) {
            return false;
          }

          // If employment has ended, exclude
          if (
            employee.employmentEndDate &&
            new Date(employee.employmentEndDate) < today
          ) {
            return false;
          }

          return true;
        });

        setEmployees(filteredEmployees);
      } catch (error) {
        console.error("Error fetching employees:", error);
      }
    };

    const fetchEmployeeMachines = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/v1/getAllAssignments`);
        setEmployeeMachines(response.data);
      } catch (error) {
        console.error("Error fetching employee machine assignments:", error);
      }
    };

    const fetchAllMachines = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/v1/getAllMachines`);
        setAllMachines(response.data);
      } catch (error) {
        console.error("Error fetching all machines:", error);
      }
    };

    const fetchLieferanten = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/v1/getLieferant`);
        // API returns { data: [...] }, so we need to access response.data.data or response.data
        const lieferantData = response.data.data || response.data;
        setLieferanten(Array.isArray(lieferantData) ? lieferantData : []);
      } catch (error) {
        console.error("Error fetching lieferanten:", error);
        setLieferanten([]); // Set empty array on error
      }
    };

    fetchTemplates();
    fetchEmployees();
    fetchEmployeeMachines();
    fetchAllMachines();
    fetchLieferanten();
  }, [apiBaseUrl]);

  // --- Drag & Drop / Process Order Functions ---
  const handleDropName = (employeeInfo, index) => {
    // Check if employeeInfo is valid
    if (!employeeInfo || !employeeInfo._id) {
      console.error("Invalid employee info:", employeeInfo);
      return;
    }

    // Store the process index for later machine selection
    setSelectedProcessIndex(index);
    setSelectedEmployeeForMachine(employeeInfo);
    setNoMachineSelected(false); // Reset checkbox state
    setShowMachineModal(true);
  };

  const assignEmployeeWithMachine = (machine) => {
    // Validate that we have all required data
    if (!selectedEmployeeForMachine || !selectedEmployeeForMachine._id) {
      console.error("Invalid employee for machine assignment");
      setShowMachineModal(false);
      return;
    }

    // Allow null machine if "No Machine" is selected
    if (!noMachineSelected && (!machine || !machine._id)) {
      console.error("Invalid machine for assignment");
      setShowMachineModal(false);
      return;
    }

    if (selectedProcessIndex === null || selectedProcessIndex < 0) {
      console.error("Invalid process index:", selectedProcessIndex);
      setShowMachineModal(false);
      return;
    }

    setProcesses((prev) => {
      // Verify the process exists at the selected index
      if (!prev[selectedProcessIndex]) {
        console.error("Process not found at index:", selectedProcessIndex);
        return prev;
      }

      return prev.map((p, i) => {
        if (i === selectedProcessIndex) {
          const assignedEmployees = p.assignedEmployees || [];

          // Check if employee is already assigned
          const existingIndex = assignedEmployees.findIndex(
            (item) =>
              item.employee &&
              item.employee._id === selectedEmployeeForMachine._id,
          );

          if (existingIndex >= 0) {
            // Update existing assignment
            const updated = [...assignedEmployees];
            updated[existingIndex] = {
              employee: selectedEmployeeForMachine,
              machine: noMachineSelected ? { name: "Keine Machine" } : machine,
            };
            return { ...p, assignedEmployees: updated };
          } else {
            // Add new assignment
            return {
              ...p,
              assignedEmployees: [
                ...assignedEmployees,
                {
                  employee: selectedEmployeeForMachine,
                  machine: noMachineSelected
                    ? { name: "Keine Machine" }
                    : machine,
                },
              ],
            };
          }
        }
        return p;
      });
    });

    // Close modal (template will be saved when user clicks "Save Template" button)
    setShowMachineModal(false);
    setSelectedEmployeeForMachine(null);
    setSelectedProcessIndex(null);
  };

  const moveProcess = (dragIndex, hoverIndex) => {
    const dragged = processes[dragIndex];
    const updated = [...processes];
    updated.splice(dragIndex, 1);
    updated.splice(hoverIndex, 0, dragged);
    setProcesses(updated);
  };

  const addProcess = (newProcess) => {
    setProcesses((prev) => [
      ...prev,
      {
        ...newProcess,
        assignedEmployees: [],
        hours: newProcess.isBreak ? 1 : 8,
      },
    ]);
  };

  const deleteProcess = (index) => {
    setProcesses((prev) => prev.filter((_, i) => i !== index));
  };

  // --- Process Hours Handling (with splitting if > 8 hours) ---
  const updateProcessHours = (index, newHours) => {
    setProcesses((prev) => {
      const current = prev[index];
      if (!current) return prev;
      const clamped = Math.max(0.5, Math.min(newHours, 24));
      if (clamped <= 8) {
        const updated = [...prev];
        updated[index] = { ...current, hours: clamped };
        return updated;
      } else {
        const fullBoxes = Math.floor(clamped / 8);
        const remainder = clamped % 8;
        const newBoxes = [];
        for (let i = 0; i < fullBoxes; i++) {
          newBoxes.push({ ...current, hours: 8 });
        }
        if (remainder > 0) {
          newBoxes.push({ ...current, hours: remainder });
        }
        const updated = [...prev];
        updated.splice(index, 1, ...newBoxes);
        return updated;
      }
    });
  };

  const incrementHours = (index) => {
    const proc = processes[index];
    if (proc) updateProcessHours(index, proc.hours + 0.5);
  };

  const decrementHours = (index) => {
    const proc = processes[index];
    if (proc) updateProcessHours(index, proc.hours - 0.5);
  };

  const validateTemplateName = async (name) => {
    if (!name || !name.trim()) return false;
    try {
      const templateNameRegex = name.trim();
      const response = await axios.get(
        `${apiBaseUrl}/v1/validateTemplateName/${encodeURIComponent(
          templateNameRegex,
        )}`,
      );
      return response.data?.found;
    } catch (error) {
      console.error("Error validating template name:", error);
      return false;
    }
  };

  // --- Save Template to Backend ---
  const saveTemplate = async () => {
    if (!templateName.trim()) {
      alert("Please enter a template name");
      return;
    }

    const existingTemplate = await validateTemplateName(templateName);
    if (existingTemplate) {
      toast.error(
        "Der Vorlagenname existiert bereits. Bitte wählen Sie einen anderen Namen.",
      );
      return;
    }

    // Create a new processed version of processes that includes machine data
    const processesWithMachineData = processes.map((process) => {
      if (
        !process.assignedEmployees ||
        process.assignedEmployees.length === 0
      ) {
        return process;
      }

      // Add machine data to each assigned employee
      const assignedEmployeesWithMachineData = process.assignedEmployees.map(
        (assignment) => {
          // Determine if this is a Lieferant or Employee based on the presence of 'konto' field
          const isLieferant = assignment.employee.konto !== undefined;

          return {
            employee: {
              employeeId: assignment.employee._id,
              name: isLieferant
                ? assignment.employee.bezeichnung || assignment.employee.name
                : assignment.employee.name,
              type: isLieferant ? "Lieferant" : "Employee",
            },
            machine: assignment.machine
              ? assignment.machine.name === "Keine Machine"
                ? { machinename: "Keine Machine" }
                : {
                    machineId: assignment.machine._id,
                    name: assignment.machine.name,
                    prozess: assignment.machine.prozess,
                    ranking: assignment.machine.ranking,
                    image: assignment.machine.image,
                    manufacture: assignment.machine.manufacture,
                    typeOfMachine: assignment.machine.typeOfMachine,
                  }
              : null,
          };
        },
      );

      return {
        ...process,
        assignedEmployees: assignedEmployeesWithMachineData,
      };
    });

    const newTemplate = {
      name: templateName,
      processes: processesWithMachineData,
    };

    try {
      const response = await axios.post(
        `${apiBaseUrl}/v1/CreateCaledarTemplate`,
        newTemplate,
      );
      setSavedTemplates((prev) => [...prev, response.data.template]);
      setTemplateName("");
      toast.success("Vorlage erfolgreich gespeichert!");
    } catch (error) {
      console.error("Error saving template:", error);
    }
  };

  // --- Download Template as a File ---
  const downloadTemplate = () => {
    if (!templateName.trim()) {
      alert("Please enter a template name to download.");
      return;
    }

    // Create a new processed version of processes that includes machine data
    const processesWithMachineData = processes.map((process) => {
      if (
        !process.assignedEmployees ||
        process.assignedEmployees.length === 0
      ) {
        return process;
      }

      // Add machine data to each assigned employee
      const assignedEmployeesWithMachineData = process.assignedEmployees.map(
        (assignment) => {
          // Determine if this is a Lieferant or Employee based on the presence of 'konto' field
          const isLieferant = assignment.employee.konto !== undefined;

          return {
            employee: {
              employeeId: assignment.employee._id,
              name: isLieferant
                ? assignment.employee.bezeichnung || assignment.employee.name
                : assignment.employee.name,
              type: isLieferant ? "Lieferant" : "Employee",
            },
            machine: assignment.machine
              ? assignment.machine.name === "Keine Machine"
                ? { machinename: "Keine Machine" }
                : {
                    machineId: assignment.machine._id,
                    name: assignment.machine.name,
                    prozess: assignment.machine.prozess,
                    ranking: assignment.machine.ranking,
                    image: assignment.machine.image,
                    manufacture: assignment.machine.manufacture,
                    typeOfMachine: assignment.machine.typeOfMachine,
                  }
              : null,
          };
        },
      );

      return {
        ...process,
        assignedEmployees: assignedEmployeesWithMachineData,
      };
    });

    const newTemplate = {
      name: templateName,
      processes: processesWithMachineData,
    };

    const fileData = JSON.stringify(newTemplate, null, 2);
    const blob = new Blob([fileData], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${templateName}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // --- Get available machines for employee ---
  const getEmployeeMachines = (employeeId) => {
    const machines = employeeMachines
      .filter(
        (assignment) =>
          assignment.employee && assignment.employee._id === employeeId,
      )
      .sort((a, b) => {
        // First, prioritize main skills (isMainSkill: true comes first)
        if (a.isMainSkill && !b.isMainSkill) return -1;
        if (!a.isMainSkill && b.isMainSkill) return 1;

        // If both have same isMainSkill status, sort by machine ranking (lower ranking = higher priority)
        if (a.machine?.ranking && b.machine?.ranking) {
          return a.machine.ranking - b.machine.ranking;
        }

        // If one has ranking and other doesn't, prioritize the one with ranking
        if (a.machine?.ranking && !b.machine?.ranking) return -1;
        if (!a.machine?.ranking && b.machine?.ranking) return 1;

        // Finally, sort alphabetically by machine name
        const nameA = a.machine?.name || "";
        const nameB = b.machine?.name || "";
        return nameA.localeCompare(nameB);
      })
      .map((assignment) => assignment.machine);

    // Return machines (the caller will check length and handle UI)
    console.log("Machines for employee:", employeeId, machines);
    console.log(
      "Sorted machines with main skills first:",
      machines.map((m) => ({
        name: m.name,
        isMainSkill: employeeMachines.find((em) => em.machine._id === m._id)
          ?.isMainSkill,
      })),
    );
    return machines;
  };

  // Function to handle direct machine assignment when employee has no machines
  const handleAssignMachine = (employee, process, machine) => {
    // Create a temporary machine assignment for this template
    setProcesses((prev) => {
      if (!prev[selectedProcessIndex]) {
        return prev;
      }

      return prev.map((p, i) => {
        if (i === selectedProcessIndex) {
          const assignedEmployees = p.assignedEmployees || [];

          return {
            ...p,
            assignedEmployees: [
              ...assignedEmployees,
              {
                employee: employee,
                machine: machine,
              },
            ],
          };
        }
        return p;
      });
    });

    // Close the machine modal
    setShowMachineModal(false);
    setSelectedEmployeeForMachine(null);
    setSelectedProcessIndex(null);
  };

  // --- Draggable Components ---
  const EmployeeBox = ({ employee }) => {
    const [, drag] = useDrag(() => ({
      type: ItemTypes.NAME,
      item: { employee },
    }));
    return (
      <div
        ref={drag}
        className="p-3 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded shadow hover:shadow-md cursor-pointer transition"
      >
        {employee.name}
      </div>
    );
  };

  const LieferantBox = ({ lieferant }) => {
    const [, drag] = useDrag(() => ({
      type: ItemTypes.NAME,
      item: { employee: lieferant },
    }));
    return (
      <div
        ref={drag}
        className="p-3 bg-gradient-to-r from-green-100 to-green-50 dark:from-green-900 dark:to-green-800 text-gray-800 dark:text-gray-200 rounded shadow hover:shadow-md cursor-pointer transition border border-green-300 dark:border-green-700"
      >
        <div className="font-bold text-sm text-green-800 dark:text-green-300">
          {lieferant.konto}
        </div>
        <div className="text-xs mt-1">{lieferant.bezeichnung}</div>
      </div>
    );
  };

  const ProcessBox = ({ process, index }) => {
    const [, drag, preview] = useDrag(() => ({
      type: ItemTypes.PROCESS,
      item: { index },
    }));

    const [, drop] = useDrop(() => ({
      accept: ItemTypes.PROCESS,
      hover: (item) => {
        if (item.index !== index) {
          moveProcess(item.index, index);
          item.index = index;
        }
      },
    }));

    const [, dropName] = useDrop(() => ({
      accept: ItemTypes.NAME,
      drop: (item) => handleDropName(item.employee, index),
    }));

    return (
      <div
        ref={(node) => preview(drop(dropName(node)))}
        className="relative w-64 rounded overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm"
      >
        <div
          ref={drag}
          className="p-3 text-center font-bold cursor-move relative text-white"
          style={{
            backgroundColor:
              processColors[process.isBreak ? "break" : process.id],
          }}
        >
          {/* Add icon component */}
          {processIcons[process.isBreak ? "break" : process.id] &&
            React.createElement(
              processIcons[process.isBreak ? "break" : process.id],
              { className: "inline-block mr-2" },
            )}
          {process.label}
          <button
            onClick={() => deleteProcess(index)}
            className="absolute top-1 right-1 text-xs text-red-200 hover:text-red-400"
          >
            &times;
          </button>
        </div>
        {!process.isBreak && (
          <div className="p-2 bg-gray-50 dark:bg-gray-800 text-left text-gray-700 dark:text-gray-200 border-t border-gray-200 dark:border-gray-700">
            {process.assignedEmployees &&
            process.assignedEmployees.length > 0 ? (
              <div className="space-y-2 mt-1">
                {process.assignedEmployees.map((assignment, idx) => (
                  <div
                    key={idx}
                    className="bg-gray-100 dark:bg-gray-700 rounded p-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    <div className="flex justify-between items-center mb-1.5">
                      <div className="font-bold text-gray-800 dark:text-white flex items-center">
                        <span className="bg-blue-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs mr-1.5">
                          {idx + 1}
                        </span>
                        {assignment.employee.name}
                      </div>
                    </div>

                    {assignment.machine &&
                      assignment.machine.name !== "Keine Machine" && (
                        <div className="mt-2 bg-white dark:bg-gray-900 rounded-lg shadow-md overflow-hidden border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow">
                          {/* Enhanced machine image section with larger size and better overlay */}
                          <div className="w-full h-48 relative">
                            {" "}
                            {/* Increased height for better visibility */}
                            <img
                              src={
                                assignment.machine.image ||
                                `/machines/${assignment.machine.name.replace(
                                  / /g,
                                  "_",
                                )}.jpg`
                              }
                              alt={assignment.machine.name}
                              className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                              onError={(e) => {
                                e.target.onerror = null;
                                // Try different machine file formats
                                const machineName =
                                  assignment.machine.name.replace(/ /g, "_");
                                // Check for files with slightly different formats
                                const tryImageInDifferentFormat = async () => {
                                  try {
                                    // Try different standardized possibilities from the /machines directory
                                    const possibleFormats = [
                                      `/machines/${machineName}.jpg`,
                                      `/machines/${machineName}.png`,
                                      `/machines/${machineName}.jpeg`,
                                      `/machines/CNC_${machineName}.jpg`,
                                      `/machines/Konventionell_${machineName}.jpg`,
                                      // Try to match image with machine type variations
                                      `/machines/${assignment.machine.typeOfMachine?.replace(
                                        / /g,
                                        "_",
                                      )}.jpg`,
                                      `/machines/${assignment.machine.prozess?.replace(
                                        / /g,
                                        "_",
                                      )}.jpg`,
                                    ];

                                    // Try to load the image from the list of possible formats
                                    for (const format of possibleFormats) {
                                      const testImg = new Image();
                                      testImg.onload = () => {
                                        e.target.src = format;
                                        return;
                                      };
                                      testImg.src = format;
                                    }

                                    // Fallback to default after trying all options
                                    setTimeout(() => {
                                      if (e.target.src.includes("undefined")) {
                                        e.target.src =
                                          "/machines/Konventionell_Fräsen_Union Bohrwerk.jpg";
                                      }
                                    }, 500);
                                  } catch (err) {
                                    console.error(
                                      "Error loading machine image:",
                                      err,
                                    );
                                    e.target.src =
                                      "/machines/Konventionell_Fräsen_Union Bohrwerk.jpg";
                                  }
                                };

                                tryImageInDifferentFormat();
                              }}
                            />
                            {/* Enhanced gradient overlay for better text readability */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent"></div>
                            {/* Machine name overlay with larger font */}
                            <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                              <div className="font-bold text-lg mb-1">
                                {assignment.machine.name}
                              </div>
                            </div>
                            {/* Ranking badge with star icon - larger and more prominent */}
                            {assignment.machine.ranking && (
                              <div className="absolute top-3 right-3 bg-gradient-to-r from-blue-700 to-blue-500 text-white font-bold px-3 py-1.5 rounded-full flex items-center shadow-md transform rotate-3 border border-blue-300">
                                <svg
                                  className="w-4 h-4 mr-1 text-yellow-300 animate-spin-slow"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                Rang #{assignment.machine.ranking}
                              </div>
                            )}
                            {/* Process badge - larger and more prominent */}
                            {assignment.machine.prozess && (
                              <div className="absolute top-3 left-3 bg-gradient-to-r from-green-600 to-green-500 text-white font-bold px-3 py-1.5 rounded-lg flex items-center shadow-md border border-green-400 animate-pulse-slow">
                                <svg
                                  className="w-4 h-4 mr-1"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                {assignment.machine.prozess}
                              </div>
                            )}
                          </div>

                          {/* Enhanced metadata with clearer layout */}
                          <div className="p-3 bg-gray-50">
                            <div className="space-y-2">
                              {/* Manufacturer with improved design */}
                              {assignment.machine.manufacture && (
                                <div className="flex items-center bg-blue-50 p-2 rounded-lg hover:bg-blue-100 transition-colors">
                                  <div className="bg-gradient-to-r from-blue-600 to-blue-400 text-white text-xs font-semibold px-2.5 py-1.5 rounded-lg shadow-sm mr-2 flex items-center">
                                    <svg
                                      className="w-3.5 h-3.5 mr-1"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M3 5a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V5zm11 1H6v8l4-2 4 2V6z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    Hersteller
                                  </div>
                                  <span className="text-sm font-medium text-gray-700 font-bold">
                                    {assignment.machine.manufacture}
                                  </span>
                                </div>
                              )}

                              {/* Machine type with improved design */}
                              {assignment.machine.typeOfMachine && (
                                <div className="flex items-center bg-purple-50 p-2 rounded-lg hover:bg-purple-100 transition-colors">
                                  <div className="bg-gradient-to-r from-purple-600 to-purple-400 text-white text-xs font-semibold px-2.5 py-1.5 rounded-lg shadow-sm mr-2 flex items-center">
                                    <svg
                                      className="w-3.5 h-3.5 mr-1"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M9 3a1 1 0 012 0v5.5a.5.5 0 001 0V4a1 1 0 112 0v7a7 7 0 11-14 0V8a1 1 0 112 0v2.5a.5.5 0 001 0V3z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    Maschinentyp
                                  </div>
                                  <span className="text-sm font-medium text-gray-700 font-bold">
                                    {assignment.machine.typeOfMachine}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                    {(!assignment.machine ||
                      assignment.machine.name === "Keine Machine") && (
                      <div className="mt-2 bg-gray-50 border border-gray-300 rounded-lg p-3 text-center">
                        <span className="text-sm text-gray-500 italic">
                          keine Maschine zugewiesen.
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-400 text-sm italic p-2">
                Mitarbeiter hier ablegen
              </div>
            )}
          </div>
        )}
        {/* <div className="flex items-center justify-center p-2 space-x-2">
          <button
            onClick={() => decrementHours(index)}
            className="w-8 h-8 bg-red-500 text-white rounded shadow hover:bg-red-600 transition"
          >
            –
          </button>
          <input
            type="number"
            value={process.hours}
            onChange={(e) =>
              updateProcessHours(index, parseFloat(e.target.value))
            }
            className="w-16 p-1 border rounded text-center"
          />
          <button
            onClick={() => incrementHours(index)}
            className="w-8 h-8 bg-green-500 text-white rounded shadow hover:bg-green-600 transition"
          >
            +
          </button>
        </div> */}
      </div>
    );
  };

  // Handler for importing a template
  const handleImportTemplate = () => {
    if (!selectedImportTemplate) {
      toast.error("Bitte wählen Sie eine Vorlage aus.");
      return;
    }
    // Set processes from the selected template
    setProcesses(selectedImportTemplate.processes || []);
    setShowImportModal(false);
    toast.success("Vorlage importiert!");
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div
        className={`flex flex-col h-screen bg-gray-100 dark:bg-gray-900 ${theme.palette.mode === "dark" ? "dark" : ""}`}
      >
        {/* Header */}
        <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm p-4 text-center">
          <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
            Vorlagendesigner
          </h1>
        </header>

        {/* Save Template Options on Top */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 flex items-center justify-center">
          <input
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="Vorlagenname"
            className="p-2 border rounded mr-2 text-gray-700 dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600"
          />
          <button
            onClick={saveTemplate}
            className="p-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition mr-2 flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            Vorlage speichern
          </button>
          <button
            onClick={downloadTemplate}
            className="p-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Vorlage herunterladen
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="p-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition ml-2 flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            Standardvorlagen importieren
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left Sidebar – Employee List & Lieferanten */}
          <aside className="w-1/5 bg-white dark:bg-gray-800 p-4 border-r border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col">
            {/* Employees Section - Scrollable */}
            <div className="flex-1 flex flex-col mb-4 min-h-0">
              <h2 className="text-xl font-semibold mb-2 text-gray-700 dark:text-gray-200">
                Mitarbeiter
              </h2>
              {/* Search Box */}
              <input
                type="text"
                value={employeeSearch}
                onChange={(e) => setEmployeeSearch(e.target.value)}
                placeholder="Suche nach Mitarbeiter..."
                className="mb-2 p-2 border border-gray-300 dark:border-gray-600 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200"
              />
              <div className="flex-1 overflow-auto space-y-2 border border-gray-200 dark:border-gray-700 rounded p-2 bg-gray-50 dark:bg-gray-900">
                {employees
                  .filter((employee) => {
                    const searchLower = employeeSearch.toLowerCase();
                    return employee.name?.toLowerCase().includes(searchLower);
                  })
                  .map((employee) => (
                    <EmployeeBox key={employee._id} employee={employee} />
                  ))}
                {employees.filter((employee) => {
                  const searchLower = employeeSearch.toLowerCase();
                  return employee.name?.toLowerCase().includes(searchLower);
                }).length === 0 && (
                  <div className="text-center text-gray-400 text-sm italic p-4">
                    Keine Mitarbeiter gefunden
                  </div>
                )}
              </div>
            </div>

            {/* Lieferanten Section - Scrollable */}
            <div className="flex-1 flex flex-col min-h-0">
              <h2 className="text-xl font-semibold mb-2 text-gray-700 dark:text-gray-200">
                Lieferanten
              </h2>
              {/* Search Box */}
              <input
                type="text"
                value={lieferantSearch}
                onChange={(e) => setLieferantSearch(e.target.value)}
                placeholder="Suche nach Konto oder Bezeichnung..."
                className="mb-2 p-2 border border-gray-300 dark:border-gray-600 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-200"
              />
              <div className="flex-1 overflow-auto space-y-2 border border-gray-200 dark:border-gray-700 rounded p-2 bg-gray-50 dark:bg-gray-900">
                {lieferanten
                  .filter((lieferant) => {
                    const searchLower = lieferantSearch.toLowerCase();
                    return (
                      lieferant.konto?.toLowerCase().includes(searchLower) ||
                      lieferant.bezeichnung?.toLowerCase().includes(searchLower)
                    );
                  })
                  .map((lieferant) => (
                    <LieferantBox key={lieferant._id} lieferant={lieferant} />
                  ))}
                {lieferanten.filter((lieferant) => {
                  const searchLower = lieferantSearch.toLowerCase();
                  return (
                    lieferant.konto?.toLowerCase().includes(searchLower) ||
                    lieferant.bezeichnung?.toLowerCase().includes(searchLower)
                  );
                }).length === 0 && (
                  <div className="text-center text-gray-400 text-sm italic p-4">
                    Keine Lieferanten gefunden
                  </div>
                )}
              </div>
            </div>
          </aside>

          {/* Center Canvas – Paper-Like Area */}
          <main className="flex-1 relative bg-white dark:bg-gray-800 m-4 rounded shadow-lg border border-gray-200 dark:border-gray-700 overflow-auto">
            <div className="p-4 flex flex-wrap gap-4">
              {processes.map((proc, index) => (
                <ProcessBox
                  key={`${proc.id}-${index}`}
                  process={proc}
                  index={index}
                />
              ))}
            </div>
            {/* Floating Plus Button */}
            <button
              onClick={() => setShowProcessModal(true)}
              className="absolute bottom-4 right-4 bg-green-500 hover:bg-green-600 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg"
            >
              <span className="text-3xl leading-none">+</span>
            </button>
          </main>

          {/* Right Sidebar – Available Processes */}
          <aside className="w-1/5 bg-white dark:bg-gray-800 p-4 border-l border-gray-200 dark:border-gray-700 overflow-auto">
            <h2 className="text-xl font-semibold mb-2 text-gray-700 dark:text-gray-200">
              Verfügbare Prozesse
            </h2>
            <div className="space-y-2">
              {availableProcesses.map((process) => {
                const IconComponent =
                  processIcons[process.isBreak ? "break" : process.id];
                return (
                  <div
                    key={process.id}
                    onClick={() => addProcess(process)}
                    className="p-2 rounded cursor-pointer text-white font-bold shadow hover:shadow-md transition flex items-center"
                    style={{
                      backgroundColor:
                        processColors[process.isBreak ? "break" : process.id],
                    }}
                  >
                    {IconComponent && (
                      <IconComponent className="mr-2" size={18} />
                    )}
                    {process.label}
                  </div>
                );
              })}
            </div>
          </aside>
        </div>

        {/* Modal for Process Selection */}
        {showProcessModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded shadow-lg w-11/12 max-w-md p-4">
              <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                  Prozess auswählen
                </h3>
                <button
                  onClick={() => setShowProcessModal(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  &times;
                </button>
              </div>
              <div className="space-y-2 max-h-60 overflow-auto">
                {availableProcesses.map((process) => {
                  const IconComponent =
                    processIcons[process.isBreak ? "break" : process.id];
                  return (
                    <div
                      key={process.id}
                      onClick={() => {
                        addProcess(process);
                        setShowProcessModal(false);
                      }}
                      className="p-2 rounded cursor-pointer text-white font-bold shadow hover:shadow-md transition flex items-center"
                      style={{
                        backgroundColor:
                          processColors[process.isBreak ? "break" : process.id],
                      }}
                    >
                      {IconComponent && (
                        <IconComponent className="mr-2" size={18} />
                      )}
                      {process.label}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Modal for Machine Selection */}
        {showMachineModal && selectedEmployeeForMachine && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-11/12 max-w-4xl overflow-hidden">
              {/* Header with gradient background */}
              <div className="bg-gradient-to-r from-blue-700 to-blue-500 text-white px-6 py-4 flex justify-between items-center">
                <h3 className="text-2xl font-bold">
                  Maschine für {selectedEmployeeForMachine.name} auswählen
                </h3>
                <button
                  onClick={() => setShowMachineModal(false)}
                  className="text-white hover:text-gray-200 text-2xl focus:outline-none"
                >
                  &times;
                </button>
              </div>

              <div className="bg-gray-50 dark:bg-gray-900 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                  Wählen Sie eine Maschine für diesen Prozess aus. Jede Maschine
                  hat unterschiedliche Fähigkeiten und Bewertungen.
                </div>
                {/* No Machine Checkbox */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="noMachineCheckbox"
                    checked={noMachineSelected}
                    onChange={(e) => setNoMachineSelected(e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
                  />
                  <label
                    htmlFor="noMachineCheckbox"
                    className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-200 cursor-pointer"
                  >
                    Keine Maschine
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-h-[65vh] overflow-auto p-6 bg-gray-100 dark:bg-gray-700">
                {(() => {
                  const employeeMachinesList = getEmployeeMachines(
                    selectedEmployeeForMachine._id,
                  );
                  return employeeMachinesList.length > 0 ? (
                    employeeMachinesList.map((machine) => (
                      <div
                        key={machine._id}
                        onClick={() => assignEmployeeWithMachine(machine)}
                        className="bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl shadow-md cursor-pointer transition-all duration-300 hover:shadow-lg hover:border-blue-300 overflow-hidden group"
                      >
                        {/* Machine Image - Enhanced larger size and better overlay */}
                        <div className="w-full h-72 relative overflow-hidden">
                          {" "}
                          {/* Increased height for better visibility */}
                          <img
                            src={
                              machine.image ||
                              `/machines/${machine.name.replace(/ /g, "_")}.jpg`
                            }
                            alt={machine.name}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            onError={(e) => {
                              e.target.onerror = null;
                              // Try different machine file formats
                              const machineName = machine.name.replace(
                                / /g,
                                "_",
                              );
                              // Check for files with slightly different formats
                              const tryImageInDifferentFormat = async () => {
                                try {
                                  // Try different standardized possibilities from the /machines directory
                                  const possibleFormats = [
                                    `/machines/${machineName}.jpg`,
                                    `/machines/${machineName}.png`,
                                    `/machines/${machineName}.jpeg`,
                                    `/machines/CNC_${machineName}.jpg`,
                                    `/machines/Konventionell_${machineName}.jpg`,
                                    // Try to match image with machine type and process variations
                                    `/machines/${machine.typeOfMachine?.replace(
                                      / /g,
                                      "_",
                                    )}.jpg`,
                                    `/machines/${machine.prozess?.replace(
                                      / /g,
                                      "_",
                                    )}.jpg`,
                                  ];

                                  // Try to load the image from the list of possible formats
                                  for (const format of possibleFormats) {
                                    const testImg = new Image();
                                    testImg.onload = () => {
                                      e.target.src = format;
                                      return;
                                    };
                                    testImg.src = format;
                                  }

                                  // Fallback to default after trying all options
                                  setTimeout(() => {
                                    if (e.target.src.includes("undefined")) {
                                      e.target.src =
                                        "/machines/Konventionell_Fräsen_Union Bohrwerk.jpg";
                                    }
                                  }, 500);
                                } catch (err) {
                                  console.error(
                                    "Error loading machine image:",
                                    err,
                                  );
                                  e.target.src =
                                    "/machines/Konventionell_Fräsen_Union Bohrwerk.jpg";
                                }
                              };

                              tryImageInDifferentFormat();
                            }}
                          />
                          {/* Enhanced gradient overlay for better text readability */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-80"></div>
                          {/* Machine name overlay at bottom of image - larger and more prominent */}
                          <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                            <h3 className="font-bold text-2xl text-shadow">
                              {machine.name}
                            </h3>
                          </div>
                          {/* Ranking badge - improved visibility with star icon */}
                          {machine.ranking && (
                            <div className="absolute top-4 right-4 bg-gradient-to-r from-blue-700 to-blue-500 text-white font-bold px-4 py-2 rounded-full shadow-lg flex items-center transform rotate-3 border-2 border-blue-300">
                              <svg
                                className="w-5 h-5 mr-2 text-yellow-300 animate-spin-slow"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              Rang #{machine.ranking}
                            </div>
                          )}
                          {/* Process badge - larger and more prominent */}
                          {machine.prozess && (
                            <div className="absolute top-4 left-4 bg-gradient-to-r from-green-600 to-green-500 text-white font-bold px-4 py-2 rounded-lg flex items-center shadow-lg border border-green-400 animate-pulse-slow">
                              <svg
                                className="w-5 h-5 mr-2"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              {machine.prozess}
                            </div>
                          )}
                          {/* Main Skill Badge - positioned at bottom left */}
                          {(() => {
                            const assignment = employeeMachines.find(
                              (em) =>
                                em.machine?._id === machine._id &&
                                em.employee?._id ===
                                  selectedEmployeeForMachine._id,
                            );
                            return assignment?.isMainSkill ? (
                              <div className="absolute bottom-4 left-4 bg-gradient-to-r from-amber-600 to-amber-500 text-white font-bold px-3 py-2 rounded-lg flex items-center shadow-lg border border-amber-400 animate-bounce">
                                <svg
                                  className="w-4 h-4 mr-2"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                HAUPT-FÄHIGKEIT
                              </div>
                            ) : null;
                          })()}
                        </div>

                        {/* Machine Details - Enhanced modern card layout with badges */}
                        <div className="p-5">
                          <div className="mb-4 space-y-4">
                            {/* Manufacturer information with enhanced styling */}
                            {machine.manufacture && (
                              <div className="flex items-center bg-blue-50 p-2 rounded-lg hover:bg-blue-100 transition-colors">
                                <span className="bg-gradient-to-r from-blue-600 to-blue-400 text-white text-xs font-semibold px-3 py-2 rounded-lg shadow-md mr-3 flex items-center">
                                  <svg
                                    className="w-5 h-5 mr-2"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M3 5a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V5zm11 1H6v8l4-2 4 2V6z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  HERSTELLER
                                </span>
                                <span className="text-sm font-medium text-gray-700 font-bold">
                                  {machine.manufacture}
                                </span>
                              </div>
                            )}

                            {/* Machine type information with enhanced styling */}
                            {machine.typeOfMachine && (
                              <div className="flex items-center bg-purple-50 p-2 rounded-lg hover:bg-purple-100 transition-colors mt-3">
                                <span className="bg-gradient-to-r from-purple-600 to-purple-400 text-white text-xs font-semibold px-3 py-2 rounded-lg shadow-md mr-3 flex items-center">
                                  <svg
                                    className="w-5 h-5 mr-2"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M9 3a1 1 0 012 0v5.5a.5.5 0 001 0V4a1 1 0 112 0v7a7 7 0 11-14 0V8a1 1 0 112 0v2.5a.5.5 0 001 0V3z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  MASCHINEN TYP
                                </span>
                                <span className="text-sm font-medium text-gray-700 font-bold">
                                  {machine.typeOfMachine}
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="pt-3 border-t border-gray-100 flex justify-end">
                            <button className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2.5 rounded-lg shadow-md transition-all duration-300 flex items-center hover:shadow-lg transform hover:-translate-y-0.5">
                              <svg
                                className="w-5 h-5 mr-2"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              Diese Maschine auswählen
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <>
                      <div className="text-center p-8 text-gray-500 border border-gray-200 rounded-xl bg-white col-span-2 shadow-inner">
                        <svg
                          className="w-16 h-16 mx-auto text-gray-300 mb-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                        <div className="text-xl font-semibold mb-2">
                          Keine Maschinen diesem Mitarbeiter zugeordnet
                        </div>
                        <p className="text-sm text-gray-500 mb-4">
                          Sie können temporär eine Maschine für diesen Prozess
                          auswählen.
                        </p>

                        <div className="bg-blue-50 p-4 rounded-lg text-blue-700 border border-blue-200 mb-4">
                          <h3 className="font-bold mb-2">
                            Verfügbare Maschinen
                          </h3>
                          <p className="text-sm">
                            Bitte wählen Sie eine der folgenden Maschinen für
                            diesen Prozess aus:
                          </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                          {(() => {
                            const employeeAssignedMachines =
                              getEmployeeMachines(
                                selectedEmployeeForMachine._id,
                              );
                            return employeeAssignedMachines.length > 0 ? (
                              employeeAssignedMachines.map((machine) => (
                                <div
                                  key={machine._id}
                                  onClick={() =>
                                    handleAssignMachine(
                                      selectedEmployeeForMachine,
                                      selectedProcessIndex,
                                      machine,
                                    )
                                  }
                                  className="bg-white hover:bg-blue-50 p-3 rounded-lg border border-gray-200 shadow-sm cursor-pointer transition-all hover:shadow-md flex flex-col"
                                >
                                  <div className="h-40 overflow-hidden rounded-lg mb-2 relative">
                                    <img
                                      src={
                                        machine.image ||
                                        `/machines/${machine.name.replace(
                                          / /g,
                                          "_",
                                        )}.jpg`
                                      }
                                      alt={machine.name}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src =
                                          "/machines/Konventionell_Fräsen_Union Bohrwerk.jpg";
                                      }}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                                    <div className="absolute bottom-0 left-0 right-0 p-2 text-white">
                                      <div className="font-bold text-sm">
                                        {machine.name}
                                      </div>
                                    </div>
                                    {/* Main Skill Badge for fallback section */}
                                    {(() => {
                                      const assignment = employeeMachines.find(
                                        (em) =>
                                          em.machine?._id === machine._id &&
                                          em.employee?._id ===
                                            selectedEmployeeForMachine._id,
                                      );
                                      return assignment?.isMainSkill ? (
                                        <div className="absolute top-2 left-2 bg-gradient-to-r from-amber-600 to-amber-500 text-white font-bold px-2 py-1 rounded flex items-center shadow-lg border border-amber-400 text-xs">
                                          <svg
                                            className="w-3 h-3 mr-1"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                          >
                                            <path
                                              fillRule="evenodd"
                                              d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                                              clipRule="evenodd"
                                            />
                                          </svg>
                                          HAUPT
                                        </div>
                                      ) : null;
                                    })()}
                                  </div>

                                  <div className="flex items-center justify-between mt-auto">
                                    {machine.prozess && (
                                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                                        {machine.prozess}
                                      </span>
                                    )}
                                    {machine.ranking && (
                                      <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded flex items-center">
                                        <svg
                                          className="w-3 h-3 mr-1"
                                          fill="currentColor"
                                          viewBox="0 0 20 20"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                        {machine.ranking}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <>
                                \n{" "}
                                <div className="col-span-2 mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                                  <div className="flex items-start">
                                    <svg
                                      className="w-6 h-6 text-amber-600 mr-3 flex-shrink-0"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    <div>
                                      <h4 className="font-bold text-amber-800 mb-1">
                                        Keine zugeordneten Maschinen
                                      </h4>
                                      <p className="text-sm text-amber-700">
                                        Diesem Mitarbeiter sind keine Maschinen
                                        zugeordnet. Sie können{" "}
                                        <strong>temporär</strong> eine Maschine
                                        aus der Liste unten auswählen oder
                                        "Keine Maschine" markieren.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                {allMachines.map((machine) => (
                                  <div
                                    key={machine._id}
                                    onClick={() =>
                                      !noMachineSelected &&
                                      assignEmployeeWithMachine(machine)
                                    }
                                    className={`bg-white border border-gray-200 rounded-xl shadow-md transition-all duration-300 overflow-hidden group ${
                                      noMachineSelected
                                        ? "opacity-50 cursor-not-allowed"
                                        : "hover:bg-blue-50 cursor-pointer hover:shadow-lg hover:border-blue-300"
                                    }`}
                                  >
                                    <div className="w-full h-72 relative overflow-hidden">
                                      <img
                                        src={
                                          machine.image ||
                                          `/machines/${machine.name.replace(
                                            / /g,
                                            "_",
                                          )}.jpg`
                                        }
                                        alt={machine.name}
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                        onError={(e) => {
                                          e.target.onerror = null;
                                          e.target.src =
                                            "/machines/Konventionell_Fräsen_Union Bohrwerk.jpg";
                                        }}
                                      />
                                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-80"></div>
                                      <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                                        <h3 className="font-bold text-2xl text-shadow">
                                          {machine.name}
                                        </h3>
                                      </div>
                                      {machine.ranking && (
                                        <div className="absolute top-4 right-4 bg-gradient-to-r from-blue-700 to-blue-500 text-white font-bold px-4 py-2 rounded-full shadow-lg flex items-center transform rotate-3 border-2 border-blue-300">
                                          <svg
                                            className="w-5 h-5 mr-2 text-yellow-300"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                          >
                                            <path
                                              fillRule="evenodd"
                                              d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                                              clipRule="evenodd"
                                            />
                                          </svg>
                                          Rang #{machine.ranking}
                                        </div>
                                      )}
                                      {machine.prozess && (
                                        <div className="absolute top-4 left-4 bg-gradient-to-r from-green-600 to-green-500 text-white font-bold px-4 py-2 rounded-lg flex items-center shadow-lg border border-green-400">
                                          <svg
                                            className="w-5 h-5 mr-2"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                          >
                                            <path
                                              fillRule="evenodd"
                                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                              clipRule="evenodd"
                                            />
                                          </svg>
                                          {machine.prozess}
                                        </div>
                                      )}
                                    </div>
                                    <div className="p-5">
                                      <div className="mb-4 space-y-4">
                                        {machine.manufacture && (
                                          <div className="flex items-center bg-blue-50 p-2 rounded-lg">
                                            <span className="bg-gradient-to-r from-blue-600 to-blue-400 text-white text-xs font-semibold px-3 py-2 rounded-lg shadow-md mr-3 flex items-center">
                                              <svg
                                                className="w-5 h-5 mr-2"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                              >
                                                <path
                                                  fillRule="evenodd"
                                                  d="M3 5a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V5zm11 1H6v8l4-2 4 2V6z"
                                                  clipRule="evenodd"
                                                />
                                              </svg>
                                              HERSTELLER
                                            </span>
                                            <span className="text-sm font-medium text-gray-700 font-bold">
                                              {machine.manufacture}
                                            </span>
                                          </div>
                                        )}
                                        {machine.typeOfMachine && (
                                          <div className="flex items-center bg-purple-50 p-2 rounded-lg">
                                            <span className="bg-gradient-to-r from-purple-600 to-purple-400 text-white text-xs font-semibold px-3 py-2 rounded-lg shadow-md mr-3 flex items-center">
                                              <svg
                                                className="w-5 h-5 mr-2"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                              >
                                                <path
                                                  fillRule="evenodd"
                                                  d="M9 3a1 1 0 012 0v5.5a.5.5 0 001 0V4a1 1 0 112 0v7a7 7 0 11-14 0V8a1 1 0 112 0v2.5a.5.5 0 001 0V3z"
                                                  clipRule="evenodd"
                                                />
                                              </svg>
                                              MASCHINEN TYP
                                            </span>
                                            <span className="text-sm font-medium text-gray-700 font-bold">
                                              {machine.typeOfMachine}
                                            </span>
                                          </div>
                                        )}
                                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                                          <span className="text-xs text-amber-700 font-semibold">
                                            ⚠️ Temporäre Auswahl
                                          </span>
                                        </div>
                                      </div>
                                      <div className="pt-3 border-t border-gray-100 flex justify-end">
                                        <button
                                          disabled={noMachineSelected}
                                          className={`font-medium px-5 py-2.5 rounded-lg shadow-md transition-all duration-300 flex items-center ${
                                            noMachineSelected
                                              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                              : "bg-blue-600 hover:bg-blue-700 text-white hover:shadow-lg transform hover:-translate-y-0.5"
                                          }`}
                                        >
                                          <svg
                                            className="w-5 h-5 mr-2"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                          >
                                            <path
                                              fillRule="evenodd"
                                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                              clipRule="evenodd"
                                            />
                                          </svg>
                                          Diese Maschine auswählen
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Footer with action buttons */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                <button
                  onClick={() => setShowMachineModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Abbrechen
                </button>
                {noMachineSelected && (
                  <button
                    onClick={() => assignEmployeeWithMachine(null)}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold flex items-center"
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Ohne Maschine speichern
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Import Templates Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded shadow-lg w-11/12 max-w-lg p-6">
              <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                  Standardvorlagen importieren
                </h3>
                <button
                  onClick={() => setShowImportModal(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl"
                >
                  &times;
                </button>
              </div>
              <div className="space-y-2 max-h-80 overflow-auto">
                {savedTemplates.length === 0 && (
                  <div className="text-gray-500 dark:text-gray-400 text-center py-4">
                    Keine gespeicherten Vorlagen gefunden.
                  </div>
                )}
                {savedTemplates.map((tmpl, idx) => (
                  <div
                    key={tmpl._id || idx}
                    onClick={() => setSelectedImportTemplate(tmpl)}
                    className={`p-3 rounded cursor-pointer border font-bold flex items-center justify-between transition ${
                      selectedImportTemplate === tmpl
                        ? "bg-blue-100 dark:bg-blue-900 border-blue-400 dark:border-blue-500 text-gray-800 dark:text-white"
                        : "bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-gray-600"
                    }`}
                  >
                    <span>{tmpl.name}</span>
                    <div className="flex items-center gap-2">
                      {selectedImportTemplate === tmpl && (
                        <span className="text-blue-600 dark:text-blue-300 font-semibold">
                          Ausgewählt
                        </span>
                      )}
                      <button
                        onClick={(e) => handleDeleteTemplate(tmpl._id, e)}
                        className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition"
                        title="Vorlage löschen"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-end mt-6 gap-2">
                <button
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleImportTemplate}
                  disabled={!selectedImportTemplate}
                  className={`px-4 py-2 rounded text-white font-semibold transition ${
                    selectedImportTemplate
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-blue-300 dark:bg-blue-800 cursor-not-allowed"
                  }`}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <ToastContainer />
    </DndProvider>
  );
};

export default TemplateMaker;
